// Analytics event tracking utility
// This provides a centralized way to track conversion events

type EventName =
  | "page_view"
  | "view_product"
  | "add_to_cart"
  | "remove_from_cart"
  | "begin_checkout"
  | "purchase"
  | "add_to_wishlist"
  | "newsletter_signup"
  | "generate_lead"
  | "search"
  | "share"
  | "use_tool";

// Google Ads conversion label for lead form submissions, e.g.
// "AW-18078735015/AbC-D_efG". Left empty until the conversion action exists in
// Google Ads; when empty no Google Ads conversion hit is sent.
export const GOOGLE_ADS_LEAD_SEND_TO = "";
export const GOOGLE_ADS_CHECKOUT_SEND_TO = "AW-18078735015/mV99CLyDpJ8dEKe1zqxD";


interface EventData {
  product_id?: string;
  product_name?: string;
  product_price?: number;
  quantity?: number;
  currency?: string;
  value?: number;
  search_term?: string;
  category?: string;
  [key: string]: string | number | boolean | undefined;
}

export const trackEvent = (event: EventName, data?: EventData) => {
  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Analytics] ${event}`, data);
  }

  // Google Analytics 4 (if gtag is available)
  if (typeof window !== "undefined" && "gtag" in window) {
    const gtag = (window as any).gtag;
    gtag("event", event, data);
    if (event === "generate_lead" && GOOGLE_ADS_LEAD_SEND_TO) {
      gtag("event", "conversion", { send_to: GOOGLE_ADS_LEAD_SEND_TO });
    }
    // Google Ads "LoftLoom Checkout click" conversion. Beacon transport so the hit
    // survives the immediate same-tab hand-off to checkout.
    if (event === "begin_checkout") {
      gtag("event", "conversion", { send_to: GOOGLE_ADS_CHECKOUT_SEND_TO, transport_type: "beacon" });
    }
  }

  // Facebook Pixel (if fbq is available)
  if (typeof window !== "undefined" && "fbq" in window) {
    const fbq = (window as any).fbq;
    const fbEventMap: Record<string, string> = {
      view_product: "ViewContent",
      add_to_cart: "AddToCart",
      begin_checkout: "InitiateCheckout",
      purchase: "Purchase",
      add_to_wishlist: "AddToWishlist",
      generate_lead: "Lead",
      search: "Search",
    };
    const fbEvent = fbEventMap[event];
    if (fbEvent) {
      fbq("track", fbEvent, data);
    }
  }

  // TikTok Pixel (already loaded in index.html)
  if (typeof window !== "undefined" && "ttq" in window) {
    const ttq = (window as any).ttq;
    const tiktokEventMap: Record<string, string> = {
      view_product: "ViewContent",
      add_to_cart: "AddToCart",
      begin_checkout: "InitiateCheckout",
      purchase: "CompletePayment",
      add_to_wishlist: "AddToWishlist",
      generate_lead: "SubmitForm",
      search: "Search",
    };
    const tiktokEvent = tiktokEventMap[event];
    if (tiktokEvent) {
      const tiktokPayload: Record<string, unknown> = {};
      if (tiktokEvent === "Search") {
        if (data?.search_term !== undefined) tiktokPayload.query = data.search_term;
      } else if (data) {
        if (data.product_id !== undefined) tiktokPayload.content_id = data.product_id;
        if (data.product_name !== undefined) tiktokPayload.content_name = data.product_name;
        if (data.product_price !== undefined) {
          tiktokPayload.value = data.product_price;
        } else if (data.value !== undefined) {
          tiktokPayload.value = data.value;
        }
        if (data.currency !== undefined) tiktokPayload.currency = data.currency;
        tiktokPayload.content_type = "product";
      }
      ttq.track(tiktokEvent, tiktokPayload);
    }
  }
};

// Convenience wrappers
export const trackProductView = (product: {
  id: string;
  name: string;
  price: number;
  currency?: string;
}) => {
  trackEvent("view_product", {
    product_id: product.id,
    product_name: product.name,
    product_price: product.price,
    currency: product.currency || "USD",
  });
};

export const trackAddToCart = (product: {
  id: string;
  name: string;
  price: number;
  quantity: number;
  currency?: string;
}) => {
  trackEvent("add_to_cart", {
    product_id: product.id,
    product_name: product.name,
    product_price: product.price,
    quantity: product.quantity,
    currency: product.currency || "USD",
    value: product.price * product.quantity,
  });
};

export const trackBeginCheckout = (value: number, currency = "USD") => {
  trackEvent("begin_checkout", { value, currency });
};

export const trackPurchase = (orderId: string, value: number, currency = "USD") => {
  trackEvent("purchase", {
    transaction_id: orderId,
    value,
    currency,
  });
};

export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  trackEvent("search", {
    search_term: searchTerm,
    results_count: resultsCount,
  });
};

/**
 * Fire once a lead form has been successfully stored — never on submit or error.
 * `formName` identifies the form (e.g. "contact", "design_plan").
 */
export const trackLead = (formName: string) => {
  trackEvent("generate_lead", {
    form: formName,
    value: 0,
    currency: "USD",
  });
};
